/**
 * The live run: grading one draw, and the bookkeeping the reveal card is built from
 * (SPEC §2 `state/session.ts`, §1, TASKS T47).
 *
 * Split in two on purpose.
 *
 *  - `gradeDraw()` is **pure**. Answer in, `Attempt` and updated `Memory` out. It composes the
 *    validator and the FSRS gate and adds nothing of its own, so the two invariants that run
 *    through it — only a production, unhinted, non-repeat attempt advances an interval — stay
 *    provable in a unit test with no store, no clock and no DOM.
 *  - `RunSession` holds the draw cursor and the per-run counters. Also pure: times are passed in.
 *
 * Neither writes anything. Persistence is the caller's, and deliberately so: the guarantee in
 * DoD-5 is that the attempt is **on disk before feedback renders**, and that ordering belongs where
 * the `bank` transition is dispatched, not buried inside a grading helper that could quietly start
 * returning before its write settled.
 */

import type { RoundDraw } from '../round/builder';
import { applyAttempt } from '../scheduler/gate';
import type { SchedulerConfig } from '../scheduler/fsrs';
import type { Attempt, ItemRecord, Memory } from '../store/schema';
import { grade, type Grade } from '../lang/validator';
import type { ClassifyContext } from '../lang/nearmiss';
import { attemptId } from '../core/ids';
import type { RunSummary } from './gamestate';

/** Everything one graded draw produces. Nothing here has been persisted yet. */
export interface DrawOutcome {
  attempt: Attempt;
  /** The item as it should now be stored: new memory, new failure count. */
  record: ItemRecord;
  /** Byte-identical to the item's previous memory when the gate refused the attempt. */
  memoryBefore: Memory;
  grade: Grade;
}

export interface GradeDrawInput {
  draw: RoundDraw;
  /** What the learner typed. Empty string is "no sé" — a real answer, graded as a failure. */
  answer: string;
  /**
   * What to grade against, when the mode's target is not simply the item's `es` (T49 onward).
   *
   * Defaults to `draw.item.es`. It is the *mode* that knows what its card asked for — a conjugation
   * slot wants one inflected form and a conversión wants the word its rule generates — and a card
   * that asks one question while grading another is the one bug in this loop the learner cannot
   * diagnose from the feedback.
   */
  expected?: string;
  /**
   * What the classifier needs to name the error (T56). Supplied by the mode that built the card.
   *
   * Grading is unaffected — a right answer is right with or without it. All it changes is whether
   * the feedback can say "wrong person" instead of "one letter out".
   */
  context?: ClassifyContext;
  runId: string;
  /** From the injected clock. */
  now: number;
  latencyMs: number;
  /** A scaffold was revealed on this card (invariant 2, SPEC §6.4). */
  hinted: boolean;
  /** This item has already been drawn in this run (invariant 1). */
  isRepeatWithinRun: boolean;
  config?: SchedulerConfig;
}

/**
 * Grade one draw and compute the item's next state. Pure.
 *
 * The `Attempt` is built before the memory is touched and carries `advancedInterval` as returned by
 * the gate, not as re-derived here. One place decides, and the export records what it decided
 * (SPEC §8.3), so invariants 1 and 2 are auditable from the data rather than only from a test.
 */
export function gradeDraw(input: GradeDrawInput): DrawOutcome {
  const { draw, runId: run, now } = input;
  const item = draw.item;

  const result = grade({
    expected: input.expected ?? item.es,
    actual: input.answer,
    ...(input.context ? { context: input.context } : {}),
  });

  const gateInput = {
    mode: draw.mode,
    hinted: input.hinted,
    isRepeatWithinRun: input.isRepeatWithinRun,
    correct: result.correct,
    nearMiss: result.nearMiss,
  };
  const applied = applyAttempt(item.memory, gateInput, now, input.config);

  const attempt: Attempt = {
    id: attemptId(run, draw.index, item.id),
    itemId: item.id,
    tier: draw.tier,
    mode: draw.mode,
    at: now,
    correct: result.correct,
    hinted: input.hinted,
    isRepeatWithinRun: input.isRepeatWithinRun,
    wasDueReview: draw.wasDueReview,
    latencyMs: input.latencyMs,
    nearMiss: result.nearMiss,
    advancedInterval: applied.advancedInterval,
    runId: run,
  };

  /*
   * The failure count drives the forced encoding step on the second failure (invariant 5, T55). It
   * counts *failures of the item*, so a hinted or repeat attempt that was wrong still counts: the
   * learner not knowing it is the fact the encoding step responds to, and whether the attempt was
   * allowed to move an interval is a different question entirely.
   */
  const failureCount = result.correct ? item.failureCount : item.failureCount + 1;

  return {
    attempt,
    record: { ...item, memory: applied.memory, failureCount },
    memoryBefore: item.memory,
    grade: result,
  };
}

// ---------------------------------------------------------------------------------------------
// The run
// ---------------------------------------------------------------------------------------------

/** An interval this short is "back tomorrow" as far as the reveal card is concerned. */
const RETURNING_SOON_MS = 36 * 60 * 60 * 1000;

/** The gap the scheduler last chose for this item. Zero for one never reviewed. */
function intervalOf(memory: Memory): number {
  return memory.lastReviewAt === null ? 0 : memory.dueAt - memory.lastReviewAt;
}

/**
 * One run's draw cursor and counters.
 *
 * Holds no clock and no store. `RunSummary` is constructed from what actually happened rather than
 * from the plan, because a run that ends after four of its twenty planned draws must report four.
 */
/**
 * How many extra times a word met for the first time today comes back inside the same ronda.
 *
 * Two, so a new word is produced three times before the run ends: once at first exposure and twice
 * more with other items in between.
 */
export const REINFORCEMENT_SIGHTINGS = 2;

/**
 * Draws to leave between one sighting of an item and the next.
 *
 * A **fixed** gap, not an expanding one. RESEARCH §2 is explicit that expanding and equal-interval
 * schedules are statistically equivalent and that there is no evidence bonus in clever interval
 * shaping, so the complexity budget is not spent here. All that matters is that the sightings are
 * not massed, and any non-trivial gap achieves that.
 */
export const REINFORCEMENT_GAP = 3;

/** Tiers 3 and 4 — capped together at 25% of a rolling week (invariant 9). Never reinforced. */
const GRAMMAR_TIERS: ReadonlySet<number> = new Set([3, 4]);

export class RunSession {
  private cursor = 0;
  /**
   * Repeat sightings owed, keyed by the draw index after which each becomes eligible.
   *
   * See `next()`. This is the whole of within-run reinforcement, and it lives here rather than in
   * the round builder on purpose — see the note there.
   */
  private readonly owed: { draw: RoundDraw; after: number }[] = [];
  private drawn = 0;
  /** The item just handed out, so no selection path can hand out the same one twice in a row. */
  private lastId: string | null = null;
  private readonly seen = new Set<string>();
  private attempted = 0;
  private banked = 0;
  private readonly promoted: string[] = [];
  private readonly returning: string[] = [];
  private readonly introduced: string[] = [];

  constructor(
    readonly runId: string,
    readonly startedAt: number,
    private readonly draws: readonly RoundDraw[],
  ) {}

  /**
   * The next draw: a repeat that has come due, otherwise the next item in the plan.
   *
   * ## Within-run reinforcement
   *
   * Reported from a real session: *"I don't want it to be just intro → quiz and then never see
   * again. I want to use it several times to make sure I drill it in."* He was describing the truth.
   * FSRS's minimum interval here is a whole day, so nothing the scheduler produces can fall due
   * inside the session that created it — a new word was shown once, asked once, and gone.
   *
   * So a newly-introduced item is owed two more sightings, spaced by `REINFORCEMENT_GAP` draws.
   *
   * ## Why here and not in the round builder
   *
   * It was written there first, inserting repeats into the plan, and the cost was not worth paying.
   * The builder's output feeds the 25% grammar cap, its displacement records, its spacing-compromise
   * accounting and the two-hundred-round simulation that proves invariant 9 — and repeats perturbed
   * every one of them. Fixing that meant rewriting the fixture that proves a hard invariant in order
   * to accommodate a comfort feature, which is the wrong way round.
   *
   * Here, the plan the builder produced is exactly the plan the cap approved. Reinforcement is a
   * property of how the session *plays* that plan, and nothing upstream can tell the difference.
   *
   * ## What a repeat is worth
   *
   * Nothing, deliberately. `advancesInterval` has always returned false for a repeat within a run,
   * so a second sighting moves no FSRS interval, feeds no tier gate, and is excluded from the
   * retention figure — RESEARCH §2 says so in as many words. It is practice, and only practice.
   */
  next(): RoundDraw | null {
    const dueRepeat = this.owed.findIndex(
      (owed) => owed.after <= this.drawn && owed.draw.item.id !== this.lastId,
    );
    if (dueRepeat !== -1) return this.take(this.owed.splice(dueRepeat, 1)[0]!.draw);

    const draw = this.draws[this.cursor++];
    if (draw) {
      const taken = this.take(draw);
      if (this.shouldReinforce(draw)) {
        /*
         * Staggered per item, from its own index in the plan.
         *
         * Without the stagger every item's repeat falls at the same distance from its first
         * sighting, and the opening of the run comes back as the same cards in the same order — a
         * learner answers that by reading a sequence rather than by retrieving a word, which is the
         * exact failure retrieval practice exists to prevent. Derived from `draw.index` rather than
         * drawn from a random stream: the plan is already seeded, and a run must replay identically
         * (DoD-14).
         */
        const stagger = draw.index % REINFORCEMENT_GAP;
        for (let n = 1; n <= REINFORCEMENT_SIGHTINGS; n += 1) {
          this.owed.push({ draw, after: this.drawn + n * (REINFORCEMENT_GAP + 1) + stagger });
        }
      }
      return taken;
    }

    /*
     * The plan is spent. Anything still owed is played out rather than dropped — a word introduced
     * by the last card has earned its repetitions as much as the first one did — but never two of
     * the same item back to back, which is the massed practice this is trying to avoid. Draining in
     * list order did exactly that, and produced `a b a a b b` on a two-item run.
     */
    if (this.owed.length === 0) return null;
    const index = this.drainIndex();
    return this.take(this.owed.splice(index, 1)[0]!.draw);
  }

  /**
   * Which owed repeat to drain next: the one with the **most copies still owed**, skipping whatever
   * was just drawn.
   *
   * Taking them in list order strands copies against each other early — `a b c a b a b c c`.
   * Draining the most-owed item first is the standard fix for scheduling with a cooldown, and it
   * keeps a stock of alternatives in hand for as long as one exists.
   *
   * One case it cannot fix, and the limitation is real: the **last** new word of a run owes its
   * repeats after every other word has already spent theirs, so its final two sightings can land
   * back to back. Nothing else is left to put between them. Everything earlier in the run is
   * properly spaced, and the alternative — dropping the repetitions of whichever word happened to
   * come last — would be worse for the one word that has had the least practice.
   */
  private drainIndex(): number {
    const remaining = new Map<string, number>();
    for (const owed of this.owed) {
      remaining.set(owed.draw.item.id, (remaining.get(owed.draw.item.id) ?? 0) + 1);
    }

    let best = -1;
    let bestCount = -1;
    for (let i = 0; i < this.owed.length; i += 1) {
      const id = this.owed[i]!.draw.item.id;
      if (id === this.lastId) continue;
      const count = remaining.get(id) ?? 0;
      if (count > bestCount) {
        best = i;
        bestCount = count;
      }
    }
    // Everything left is the item just drawn — a one-item run, where nothing can separate them.
    return best === -1 ? 0 : best;
  }

  /** Hand a draw out, remembering it so the next selection can avoid repeating it immediately. */
  private take(draw: RoundDraw): RoundDraw {
    this.drawn += 1;
    this.lastId = draw.item.id;
    return draw;
  }

  /**
   * Which draws earn repetition.
   *
   * **New items only.** A due review is already spaced by FSRS at the interval it chose; drilling it
   * again the same evening is the massed practice this design exists to avoid.
   *
   * **Never Motor or Conversión.** Invariant 9 caps them at 25% of a rolling week, and a repeat is
   * grammar *time* whether or not it is grammar *content*. The cap exists because grammar is what a
   * learner over-weights; tripling its share through a side door is what the invariant is written to
   * prevent. That is a real limitation, and it is the right one.
   */
  private shouldReinforce(draw: RoundDraw): boolean {
    if (draw.wasDueReview) return false;
    if (draw.item.memory.state !== 'new') return false;
    if (GRAMMAR_TIERS.has(draw.tier)) return false;
    return !this.seen.has(draw.item.id);
  }

  /** Repeat sightings still owed. Never shown to the learner — the run is mystery-composition. */
  owedRepeats(): number {
    return this.owed.length;
  }

  /** Draws remaining in the plan. Never shown to the learner — the run is mystery-composition. */
  remaining(): number {
    return Math.max(0, this.draws.length - this.cursor) + this.owed.length;
  }

  /** Has this item already been drawn in this run? Invariant 1's repeat test. */
  hasSeen(itemId: string): boolean {
    return this.seen.has(itemId);
  }

  /** Called when a draw is presented, before it is answered. */
  present(draw: RoundDraw): void {
    this.attempted += 1;
    if (draw.item.memory.state === 'new') this.introduced.push(draw.item.id);
    this.seen.add(draw.item.id);
  }

  /**
   * Called once the outcome is **on disk**.
   *
   * `banked` therefore counts what survived, not what was graded, and the reveal card cannot
   * report a result that a reload would contradict.
   */
  bank(outcome: DrawOutcome, now: number): void {
    this.banked += 1;
    const before = outcome.memoryBefore;
    const after = outcome.record.memory;

    /*
     * Promotion is the *interval* getting longer, not the due date moving forward.
     *
     * Comparing due dates directly is wrong for the case that matters most: an item three days
     * overdue that the learner just failed gets a new due date later than its old one, and would
     * be reported as promoted on the card that follows getting it wrong.
     *
     * Read from the two memories rather than from whether the answer was right, so a correct but
     * hinted attempt promotes nothing (invariant 2) and the reveal card cannot claim otherwise.
     * An item seen for the first time is an introduction, not a promotion; it is listed separately.
     */
    const wasNew = before.state === 'new';
    const promoted = !wasNew && intervalOf(after) > intervalOf(before);

    if (promoted) this.promoted.push(outcome.record.id);
    else if (after.dueAt - now <= RETURNING_SOON_MS) this.returning.push(outcome.record.id);
  }

  summary(endedAt: number): RunSummary {
    return {
      runId: this.runId,
      startedAt: this.startedAt,
      endedAt,
      itemsAttempted: this.attempted,
      itemsBanked: this.banked,
      promoted: [...this.promoted],
      returningTomorrow: [...this.returning],
      // Rules unlock on tier gating, which is recomputed at boot from the attempt log; the reveal
      // card gets this from T57 rather than inventing it here.
      rulesUnlocked: [],
      newItemsIntroduced: [...this.introduced],
    };
  }
}
